import React, { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { type Event } from 'nostr-tools';
import { useNostrQuery } from '../hooks/useNostrQuery';
import {
  RADIO_TRACK_KIND, RADIO_TRACK_TAG,
  RADIO_PROMO_KIND, RADIO_PROMO_TAG,
  parseTrackSubmission, parsePromotion, formatDuration,
  getSoundtrackEnabled, setSoundtrackEnabled,
  addToPlayHistory, getPlayHistory,
  computeSongPromoAuction, computeFeaturedArtistAuction,
  type RadioTrack, type RadioPromotion,
  type SongPromoAuctionState, type FeaturedArtistAuctionState,
} from '../lib/radio';
import { FLAG_KIND, FLAG_THRESHOLD, isBanned } from '../lib/communityFlag';
import { OPERATOR_PUBKEY } from '../lib/platform';
import { getBCHPrice } from '../hooks/useBCHPrice';

// ── Types ─────────────────────────────────────────────────────────

interface RadioPlayerState {
  queue: RadioTrack[];
  currentIndex: number;
  currentTrack: RadioTrack | null;
  isPlaying: boolean;
  loading: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  muted: boolean;
  /** True once the queue has been loaded from relays at least once */
  queueReady: boolean;
  /** "Soundtrack My Feed" mode — music keeps playing while browsing */
  soundtrackMode: boolean;
  /** Shuffle / random playback mode */
  shuffle: boolean;
  /** Recently played track IDs (most recent first) */
  playHistory: string[];
  /** Map of track ID → like count */
  likeCounts: Map<string, number>;
  /** Map of track ID → comment count */
  commentCounts: Map<string, number>;
  /** Map of track ID → comment events (newest first) for display */
  commentsByTrack: Map<string, Event[]>;
  /** Map of track ID → tip total sats */
  tipCounts: Map<string, number>;
  /** Set of track IDs liked by current user */
  myLikes: Set<string>;
  /** Active song promotions (not expired) */
  promotedTracks: RadioPromotion[];
  /** Active featured artist promotion (not expired), most recent wins */
  featuredArtist: RadioPromotion | null;
  /** Song promotion auction state */
  songPromoAuction: SongPromoAuctionState;
  /** Featured artist auction state */
  featuredArtistAuction: FeaturedArtistAuctionState;
}

interface RadioPlayerActions {
  togglePlay: () => void;
  skipNext: () => void;
  skipPrev: () => void;
  seekTo: (pct: number) => void;
  setVolume: (v: number) => void;
  toggleMute: () => void;
  playTrack: (index: number) => void;
  /** Play a specific track object (finds it in queue or adds context) */
  playTrackById: (trackId: string) => void;
  /** Stop playback and hide the mini-player */
  stop: () => void;
  refetchQueue: () => void;
  /** Toggle "Soundtrack My Feed" mode */
  toggleSoundtrackMode: () => void;
  /** Toggle shuffle / random playback */
  toggleShuffle: () => void;
  /** Enable shuffle and immediately start playing a random track */
  shufflePlay: () => void;
}

type RadioPlayerContextType = RadioPlayerState & RadioPlayerActions;

const RadioPlayerContext = createContext<RadioPlayerContextType | null>(null);

export function useRadioPlayer(): RadioPlayerContextType {
  const ctx = useContext(RadioPlayerContext);
  if (!ctx) throw new Error('useRadioPlayer must be used within RadioPlayerProvider');
  return ctx;
}

// ── Provider ──────────────────────────────────────────────────────

export function RadioPlayerProvider({ children }: { children: React.ReactNode }) {
  // Persistent audio element — lives for the lifetime of the app
  const audioRef = useRef<HTMLAudioElement | null>(null);
  if (!audioRef.current && typeof Audio !== 'undefined') {
    audioRef.current = new Audio();
    audioRef.current.preload = 'auto';
  }

  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(() => {
    try { return parseFloat(localStorage.getItem('bchnostr:radio-volume') || '0.8') || 0.8; } catch { return 0.8; }
  });
  const [muted, setMuted] = useState(false);
  const [soundtrackMode, setSoundtrackMode] = useState(getSoundtrackEnabled);
  const [shuffle, setShuffle] = useState(() => {
    try { return localStorage.getItem('bchnostr:radio-shuffle') === '1'; } catch { return false; }
  });
  const [playHistory, setPlayHistory] = useState<string[]>(getPlayHistory);

  // ── Fetch tracks from relays ────────────────────────────────────
  // The radio is a permanent MUSIC LIBRARY, not a rolling feed: a song
  // submitted a year ago is just as valid as one submitted today. Do NOT
  // apply a `since` window to the track query — a 30-day cutoff silently
  // aged out the whole back-catalogue (the "426 → 60-80 songs disappeared"
  // bug: only tracks from the last month survived). We fetch the full
  // catalogue with a high cap instead.
  const TRACK_LIMIT = 3000;
  const { events: trackEvents, refetch } = useNostrQuery({
    filters: [{ kinds: [RADIO_TRACK_KIND], '#t': [RADIO_TRACK_TAG], limit: TRACK_LIMIT }],
    enabled: true,
    allRelays: true,
  });

  // `since` still scopes ENGAGEMENT queries (likes/tips/comments/promos) —
  // those grow unbounded and recent activity is what matters for ranking.
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 30);

  // Flags + deletions must cover the WHOLE catalogue (no `since`), otherwise a
  // track flagged/removed long ago would reappear once it's older than the
  // engagement window. Moderation is not a rolling window.
  const { events: flagEvents } = useNostrQuery({
    filters: [{ kinds: [FLAG_KIND], '#t': ['bch-radio-flag'], limit: 1000 }],
    enabled: true,
  });

  // Fetch deletion requests (NIP-09 kind 5) for radio tracks
  const { events: deleteEvents } = useNostrQuery({
    filters: [{ kinds: [5], '#k': [String(RADIO_TRACK_KIND)], limit: 1000 }],
    enabled: true,
  });

  // Fetch likes (kind 7) on radio tracks
  const { events: likeEvents } = useNostrQuery({
    filters: [{ kinds: [7], '#t': [RADIO_TRACK_TAG], since, limit: 500 }],
    enabled: true,
  });

  // Fetch comments (legacy kind 1 + NIP-22 kind 1111 replies to radio tracks)
  const { events: commentEvents } = useNostrQuery({
    filters: [
      { kinds: [1], '#t': ['bch-radio-comment'], since, limit: 300 },
      { kinds: [1111], '#t': ['bch-radio-comment'], since, limit: 300 },
    ],
    enabled: true,
  });

  // Fetch tip receipts — tips use 'bch-tip' tag and reference the tipped
  // event via an 'e' tag. We query all bch-tip receipts and match them
  // against known track IDs client-side.
  const { events: tipEvents } = useNostrQuery({
    filters: [{ kinds: [9735], '#t': ['bch-tip'], since, limit: 500 }],
    enabled: true,
  });

  // Fetch promotion events (kind 30078 with radio-promo tag)
  const { events: promoEvents } = useNostrQuery({
    filters: [{ kinds: [RADIO_PROMO_KIND], '#t': [RADIO_PROMO_TAG], since, limit: 100 }],
    enabled: true,
  });

  // ── Compute active promotions ──────────────────────────────────
  const activePromos = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    const active: RadioPromotion[] = [];
    for (const ev of promoEvents) {
      const promo = parsePromotion(ev);
      if (promo && promo.expiresAt > now) active.push(promo);
    }
    return active;
  }, [promoEvents]);

  const promotedTracks = useMemo(() => {
    const songPromos = activePromos.filter(p => p.type === 'song' && p.trackId);
    songPromos.sort((a, b) => b.paidSats - a.paidSats || b.createdAt - a.createdAt);
    return songPromos;
  }, [activePromos]);

  const featuredArtist = useMemo(() => {
    let best: RadioPromotion | null = null;
    for (const p of activePromos) {
      if (p.type !== 'artist') continue;
      if (!best || p.paidSats > best.paidSats || (p.paidSats === best.paidSats && p.createdAt > best.createdAt)) {
        best = p;
      }
    }
    return best;
  }, [activePromos]);

  // Auction states — recompute periodically as BCH price changes
  const bchPriceForAuction = useMemo(() => getBCHPrice() || 0, [activePromos]);
  const songPromoAuction = useMemo(() => computeSongPromoAuction(activePromos, bchPriceForAuction || undefined), [activePromos, bchPriceForAuction]);
  const featuredArtistAuction = useMemo(() => computeFeaturedArtistAuction(activePromos, bchPriceForAuction || undefined), [activePromos, bchPriceForAuction]);

  // ── Compute social stats ────────────────────────────────────────
  const likeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ev of likeEvents) {
      const trackId = ev.tags?.find((t: string[]) => t[0] === 'e')?.[1];
      if (trackId) counts.set(trackId, (counts.get(trackId) || 0) + 1);
    }
    return counts;
  }, [likeEvents]);

  const myLikes = useMemo(() => {
    const s = new Set<string>();
    // We don't have direct access to user pubkey here, so we store liked event IDs
    // and check them via likeEvents. The user check is done in components.
    return s;
  }, []);

  // Group comment events by the track they reference. NIP-22 kind 1111 uses
  // uppercase 'E' for root scope (preferred), lowercase 'e' for parent; legacy
  // kind 1 uses lowercase 'e'. We de-dupe by event id and sort newest-first.
  const commentsByTrack = useMemo(() => {
    const byTrack = new Map<string, Event[]>();
    const seen = new Set<string>();
    for (const ev of commentEvents) {
      if (seen.has(ev.id)) continue;
      seen.add(ev.id);
      const trackId = ev.tags?.find((t: string[]) => t[0] === 'E')?.[1]
        ?? ev.tags?.find((t: string[]) => t[0] === 'e')?.[1];
      if (!trackId) continue;
      const arr = byTrack.get(trackId) || [];
      arr.push(ev);
      byTrack.set(trackId, arr);
    }
    for (const arr of byTrack.values()) {
      arr.sort((a, b) => b.created_at - a.created_at);
    }
    return byTrack;
  }, [commentEvents]);

  const commentCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const [trackId, arr] of commentsByTrack) counts.set(trackId, arr.length);
    return counts;
  }, [commentsByTrack]);

  const tipCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ev of tipEvents) {
      const eventRef = ev.tags?.find((t: string[]) => t[0] === 'e')?.[1];
      if (!eventRef) continue;
      // Extract amount from the 'amount' tag (sats) or content JSON
      let amount = 0;
      const amountTag = ev.tags?.find((t: string[]) => t[0] === 'amount');
      if (amountTag) {
        amount = parseInt(amountTag[1]) || 0;
      } else {
        try {
          const data = JSON.parse(ev.content);
          amount = data.amount_sats || 0;
        } catch {}
      }
      if (amount > 0) {
        counts.set(eventRef, (counts.get(eventRef) || 0) + amount);
      }
    }
    return counts;
  }, [tipEvents]);

  // Compute track IDs deleted by their authors via NIP-09 kind 5
  const deletedTrackIds = useMemo(() => {
    const deleted = new Set<string>();
    // Build a map of track event IDs → their author pubkeys from trackEvents
    const trackAuthors = new Map<string, string>();
    for (const ev of trackEvents) {
      trackAuthors.set(ev.id, ev.pubkey);
    }
    for (const ev of deleteEvents) {
      const eTags = ev.tags?.filter((t: string[]) => t[0] === 'e') || [];
      for (const tag of eTags) {
        const trackId = tag[1];
        if (!trackId) continue;
        // Only honor deletion if the deletion author matches the track author
        const trackAuthor = trackAuthors.get(trackId);
        if (trackAuthor && trackAuthor === ev.pubkey) {
          deleted.add(trackId);
        }
      }
    }
    return deleted;
  }, [deleteEvents, trackEvents]);

  const hiddenTrackIds = useMemo(() => {
    const flagCounts = new Map<string, Set<string>>();
    const hidden = new Set<string>();
    // Include author-deleted tracks
    for (const id of deletedTrackIds) {
      hidden.add(id);
    }
    for (const ev of flagEvents) {
      const trackId = ev.tags?.find((t: string[]) => t[0] === 'e')?.[1];
      if (!trackId) continue;
      // Operator flag = instant removal, no threshold needed
      if (ev.pubkey === OPERATOR_PUBKEY) {
        hidden.add(trackId);
        continue;
      }
      if (!flagCounts.has(trackId)) flagCounts.set(trackId, new Set());
      flagCounts.get(trackId)!.add(ev.pubkey);
    }
    flagCounts.forEach((flaggers, trackId) => {
      if (flaggers.size >= FLAG_THRESHOLD) hidden.add(trackId);
    });
    return hidden;
  }, [flagEvents, deletedTrackIds]);

  const queue = useMemo(() => {
    const seen = new Set<string>();
    const tracks: RadioTrack[] = [];
    for (const ev of trackEvents) {
      if (seen.has(ev.id)) continue;
      seen.add(ev.id);
      if (hiddenTrackIds.has(ev.id)) continue;
      if (isBanned(ev.pubkey)) continue;
      const track = parseTrackSubmission(ev);
      if (track) tracks.push(track);
    }
    return tracks.sort((a, b) => a.submittedAt - b.submittedAt);
  }, [trackEvents, hiddenTrackIds]);

  const queueReady = trackEvents.length > 0 || queue.length > 0;
  const currentTrack = currentIndex >= 0 && currentIndex < queue.length ? queue[currentIndex] : null;

  // ── Track play history ──────────────────────────────────────────
  useEffect(() => {
    if (currentTrack) {
      addToPlayHistory(currentTrack.id);
      setPlayHistory(getPlayHistory());
    }
  }, [currentTrack?.id]);

  // ── Load track when index changes ───────────────────────────────
  const prevTrackUrlRef = useRef<string | null>(null);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;
    // Don't reload if same track URL
    if (prevTrackUrlRef.current === currentTrack.url) return;
    prevTrackUrlRef.current = currentTrack.url;
    audio.src = currentTrack.url;
    audio.load();
    if (isPlaying) {
      setLoading(true);
      audio.play().catch(() => setIsPlaying(false));
    }
  }, [currentIndex, currentTrack?.url]);

  // ── Audio event handlers ────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onDurationChange = () => setDuration(audio.duration || 0);
    const onPlay = () => { setIsPlaying(true); setLoading(false); };
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      if (shuffle && queue.length > 1) {
        // Pick a random track that isn't the current one
        let nextIdx: number;
        do {
          nextIdx = Math.floor(Math.random() * queue.length);
        } while (nextIdx === currentIndex && queue.length > 1);
        prevTrackUrlRef.current = null;
        setCurrentIndex(nextIdx);
        setIsPlaying(true);
      } else if (currentIndex < queue.length - 1) {
        setCurrentIndex(i => i + 1);
        setIsPlaying(true);
      } else {
        setIsPlaying(false);
      }
    };
    const onWaiting = () => setLoading(true);
    const onCanPlay = () => setLoading(false);
    const onError = () => { setLoading(false); setIsPlaying(false); };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('error', onError);
    };
  }, [currentIndex, queue.length]);

  // ── Volume sync ─────────────────────────────────────────────────
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = muted ? 0 : volume;
    }
  }, [volume, muted]);

  // ── Actions ─────────────────────────────────────────────────────

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!currentTrack && queue.length > 0) {
      setCurrentIndex(0);
      setIsPlaying(true);
      return;
    }
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(() => {});
    }
  }, [isPlaying, currentTrack, queue.length]);

  const skipNext = useCallback(() => {
    if (shuffle && queue.length > 1) {
      let nextIdx: number;
      do {
        nextIdx = Math.floor(Math.random() * queue.length);
      } while (nextIdx === currentIndex && queue.length > 1);
      prevTrackUrlRef.current = null;
      setCurrentIndex(nextIdx);
      setIsPlaying(true);
    } else if (currentIndex < queue.length - 1) {
      prevTrackUrlRef.current = null;
      setCurrentIndex(i => i + 1);
      setIsPlaying(true);
    }
  }, [currentIndex, queue.length, shuffle]);

  const skipPrev = useCallback(() => {
    const audio = audioRef.current;
    if (audio && currentTime > 3) {
      audio.currentTime = 0;
    } else if (currentIndex > 0) {
      prevTrackUrlRef.current = null;
      setCurrentIndex(i => i - 1);
      setIsPlaying(true);
    }
  }, [currentIndex, currentTime]);

  const seekTo = useCallback((pct: number) => {
    const audio = audioRef.current;
    if (audio && duration > 0) {
      audio.currentTime = pct * duration;
    }
  }, [duration]);

  const setVolume = useCallback((v: number) => {
    setVolumeState(v);
    try { localStorage.setItem('bchnostr:radio-volume', String(v)); } catch {}
  }, []);

  const toggleMute = useCallback(() => setMuted(m => !m), []);

  const playTrack = useCallback((index: number) => {
    prevTrackUrlRef.current = null;
    setCurrentIndex(index);
    setIsPlaying(true);
  }, []);

  const playTrackById = useCallback((trackId: string) => {
    const idx = queue.findIndex(t => t.id === trackId);
    if (idx >= 0) {
      prevTrackUrlRef.current = null;
      setCurrentIndex(idx);
      setIsPlaying(true);
    }
  }, [queue]);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.src = '';
    }
    prevTrackUrlRef.current = null;
    setCurrentIndex(-1);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  const toggleShuffle = useCallback(() => {
    setShuffle(prev => {
      const next = !prev;
      try { localStorage.setItem('bchnostr:radio-shuffle', next ? '1' : '0'); } catch {}
      return next;
    });
  }, []);

  const shufflePlay = useCallback(() => {
    if (queue.length === 0) return;
    // Enable shuffle mode
    setShuffle(true);
    try { localStorage.setItem('bchnostr:radio-shuffle', '1'); } catch {}
    // Pick a random track
    const randomIdx = Math.floor(Math.random() * queue.length);
    prevTrackUrlRef.current = null;
    setCurrentIndex(randomIdx);
    setIsPlaying(true);
  }, [queue.length]);

  const toggleSoundtrackMode = useCallback(() => {
    setSoundtrackMode(prev => {
      const next = !prev;
      setSoundtrackEnabled(next);
      return next;
    });
  }, []);

  // Poll for new tracks
  useEffect(() => {
    const timer = setInterval(refetch, 30000);
    return () => clearInterval(timer);
  }, [refetch]);

  const value: RadioPlayerContextType = {
    queue,
    currentIndex,
    currentTrack,
    isPlaying,
    loading,
    currentTime,
    duration,
    volume,
    muted,
    queueReady,
    soundtrackMode,
    shuffle,
    playHistory,
    likeCounts,
    commentCounts,
    commentsByTrack,
    tipCounts,
    myLikes,
    promotedTracks,
    featuredArtist,
    songPromoAuction,
    featuredArtistAuction,
    togglePlay,
    skipNext,
    skipPrev,
    seekTo,
    setVolume,
    toggleMute,
    playTrack,
    playTrackById,
    stop,
    refetchQueue: refetch,
    toggleSoundtrackMode,
    toggleShuffle,
    shufflePlay,
  };

  return (
    <RadioPlayerContext.Provider value={value}>
      {children}
    </RadioPlayerContext.Provider>
  );
}

